import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { usePostHog } from 'posthog-js/react';
// import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props";
import OAuth2Login from "react-simple-oauth2-login";

//internal import
import UserServices from "@services/UserServices";
import { UserContext } from "@context/UserContext";
import { notifyError, notifySuccess } from "@utils/toast";
import { useGoogleLogin, hasGrantedAnyScopeGoogle } from "@react-oauth/google";
import axios from "axios";
import * as gtag from "src/lib/gtag"

const useLoginSubmit = (setModalOpen) => {
  const router = useRouter();
  const { redirect } = router.query;
  const { dispatch } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const posthog = usePostHog();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const submitHandler = ({
    name,
    email,
    registerEmail,
    verifyEmail,
    password,
  }) => {
    setLoading(true);
    if (registerEmail && password) {
      UserServices.userLogin({
        registerEmail,
        password,
      })
        .then((res) => {
          setLoading(false);
          setModalOpen(false);
          router.push(redirect || "/");
          notifySuccess("Login is Successful!");
          res.verfied_through = "Login";
          dispatch({ type: "USER_LOGIN", payload: res });
          Cookies.set("userInfo", JSON.stringify(res), {
            expires: 200,
          });          // Trigger identification event instead of calling identify directly
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('identify_posthog_user'));
          }

          gtag.event({
            action: "login",
            params: {
              method: "Email"
            }
          });
          // PostHog event
          posthog?.capture('user_signed_in', {
            $login_method: "Email",
            user_id: res._id,
            email: res.email
          });
        })
        .catch((err) => {
          notifyError(err ? err.response.data.message : err.message);
          setLoading(false);
        });
    }
    if (name && email && password) {
      UserServices.verifyEmailAddress({ name, email, password })
        .then((res) => {
          setLoading(false);
          setModalOpen(false);
          notifySuccess(res.message);
          posthog?.capture('user_signed_up', {
            $login_method: "Email",
            user_id: res._id,
            email: res.email
          });
        })
        .catch((err) => {
          setLoading(false);
          notifyError(err.response.data.message);
        });
    }
    if (verifyEmail) {
      UserServices.forgetPassword({ verifyEmail })
        .then((res) => {
          setLoading(false);
          notifySuccess(res.message);
          setValue("verifyEmail");
        })
        .catch((err) => {
          setLoading(false);
          notifyError(err ? err.response.data.message : err.message);
        });
    }
  };

  const handleGoogleSignIn = useGoogleLogin({
    onSuccess: async (googleCredential) => {
      try {
        const { access_token } = googleCredential;

        const userInfo = await axios.get(
          `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${access_token}`
        );

        const user = userInfo.data;

        const res = await UserServices.signUpWithProvider({
          name: user.name,
          email: user.email,
          image: user.picture,
          verfied_through: "Google",
        }); 
        if (res) {
          setModalOpen(false);
          res.verfied_through = "Google";
          notifySuccess("Login is Successful!");
          dispatch({ type: "USER_LOGIN", payload: res });
          Cookies.set("userInfo", JSON.stringify(res), {
            expires: 200,
          });

          // Trigger identification event instead of calling identify directly
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('identify_posthog_user'));
          }

          router.push(redirect || "/");
          gtag.event({
            action: "login",
            params: {
              method: "Google"
            }
          });
          // PostHog event
          posthog?.capture('user_signed_in', {
            $login_method: "Google",
            user_id: res._id,
            email: res.email
          });
        }
      } catch (err) {
        notifyError(err ? err.response.data.message : err.message);
        setModalOpen(false);
      }
    },
  });

  const handleFacebookSignIn = async (res) => {
    const accessToken = res.access_token;

    const result = await fetch(
      `https://graph.facebook.com/me?fields=id,name,email,picture.type(large)&access_token=${accessToken}`
    );

    const profile = await result.json();

    UserServices.signUpWithProvider({
      name: profile.name,
      email: profile.email,
      image: profile.picture.data.url,
      verfied_through: "Facebook",
    }).then((res) => {
      setModalOpen(false);
      // notifySuccess("Login is Successful!");
      res.verfied_through = "Facebook";
      dispatch({ type: "USER_LOGIN", payload: res });
      Cookies.set("userInfo", JSON.stringify(res), {
        expires: 200,
      });

      // Trigger identification event instead of calling identify directly
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('identify_posthog_user'));
      }

      router.push(redirect || "/");
      notifySuccess("Login is Successful!");
      gtag.event({
        action: "login",
        params: {
          method: "Facebook"
        }
      });
      // PostHog event
      posthog?.capture('user_signed_in', {
        $login_method: "Facebook",
        user_id: res._id,
        email: res.email
      });
    })
      .catch((err) => {
        notifyError(err ? err.response.data.message : err.message);
        setModalOpen(false);
      });
  };

  const handleDiscordSignIn = () => {
    router.push(`${process.env.NEXT_PUBLIC_API_BASE_URL}/discord`);
  };

  return {
    handleSubmit,
    submitHandler,
    handleGoogleSignIn,
    register,
    errors,
    loading,
    OAuth2Login,
    handleFacebookSignIn,
    handleDiscordSignIn,
  };
};

export default useLoginSubmit;
